<template>
  <div class="post-page">
    <template v-if="post">
      <article>
        <header class="post-hero">
          <div class="container">
            <div class="row justify-content-center">
              <div class="col-lg-8">
                <nav class="post-back-nav" aria-label="Breadcrumb">
                  <router-link to="/blog" class="post-back">
                    <svg-icon name="arrow-right" :size="14" class="post-back-icon" /> All posts
                  </router-link>
                </nav>
                <p class="eyebrow">{{ post.category }}</p>
                <h1 class="hero-title post-title">{{ post.title }}</h1>
                <p class="post-meta">
                  <time :datetime="post.date">{{ formatPostDate(post.date) }}</time>
                  <span class="post-meta-dot" aria-hidden="true">&middot;</span>
                  <span>{{ post.readingMinutes }} min read</span>
                  <span class="post-meta-dot" aria-hidden="true">&middot;</span>
                  <span>By the Off The Couch team</span>
                </p>
              </div>
            </div>
          </div>
        </header>

        <div class="post-content">
          <div class="container">
            <div class="row justify-content-center">
              <div class="col-lg-8">
                <aside v-if="post.takeaways && post.takeaways.length" class="post-takeaways" aria-label="Key takeaways">
                  <p class="post-takeaways-title">Key takeaways</p>
                  <ul>
                    <li v-for="(t, i) in post.takeaways" :key="'T' + i">{{ t }}</li>
                  </ul>
                </aside>

                <component :is="post.component" />

                <section v-if="post.faq && post.faq.length" class="post-faq" aria-labelledby="post-faq-title">
                  <h2 id="post-faq-title">Frequently asked questions</h2>
                  <div v-for="(item, i) in post.faq" :key="'Q' + i" class="post-faq-item">
                    <h3>{{ item.q }}</h3>
                    <p>{{ item.a }}</p>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </article>

      <section class="cta-band">
        <div class="container">
          <div class="row justify-content-center">
            <div class="col-lg-8">
              <h2 class="cta-band-title">See it on your own venue.</h2>
              <p class="cta-band-sub">
                Every feature is included on one plan, from the first booking onward. Book a demo and we will
                set this up with your events.
              </p>
              <div class="cta-band-buttons">
                <a href="/contact" class="btn-cta-primary">Get a demo</a>
                <a href="/features" class="btn-cta-secondary">Explore features &rarr;</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <section v-else class="post-missing">
      <div class="container text-center">
        <p class="eyebrow">Blog</p>
        <h1 class="hero-title">That post is not here</h1>
        <p class="post-missing-sub">It may have moved or the link is incomplete.</p>
        <router-link to="/blog" class="btn-cta-secondary">Browse all posts &rarr;</router-link>
      </div>
    </section>
  </div>
</template>

<script>
import { findPost, formatPostDate } from '@/blog/posts';
import { applyHead, setJsonLd, clearJsonLd, absoluteUrl, ORGANIZATION, SITE_URL } from '@/blog/head';

const LD_IDS = ['post', 'breadcrumb', 'faq'];

export default {
  name: 'BlogPost',
  computed: {
    post() {
      return findPost(this.$route.params.slug);
    },
  },
  watch: {
    post: { immediate: true, handler: 'applyHead' },
  },
  beforeDestroy() {
    clearJsonLd(LD_IDS);
  },
  methods: {
    formatPostDate,
    // Title, description, canonical, share tags and structured data per post.
    // BlogPosting, BreadcrumbList and FAQPage are what search and answer
    // engines read; the FAQ block on the page and the FAQPage data come from
    // the same registry entry so they never drift apart.
    applyHead() {
      const p = this.post;
      if (!p) {
        applyHead({ title: 'Post not found', description: 'This post could not be found.', path: '/blog' });
        clearJsonLd(LD_IDS);
        return;
      }
      const path = '/blog/' + p.slug;
      applyHead({ title: p.title, description: p.excerpt, path, type: 'article', image: p.image, publishedTime: p.date });
      setJsonLd('post', {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: p.title,
        description: p.excerpt,
        datePublished: p.date,
        dateModified: p.date,
        image: absoluteUrl(p.image),
        author: ORGANIZATION,
        publisher: ORGANIZATION,
        mainEntityOfPage: { '@type': 'WebPage', '@id': SITE_URL + path },
        articleSection: p.category,
        inLanguage: 'en',
      });
      setJsonLd('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Blog', item: SITE_URL + '/blog' },
          { '@type': 'ListItem', position: 2, name: p.title, item: SITE_URL + path },
        ],
      });
      setJsonLd('faq', p.faq && p.faq.length ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: p.faq.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      } : null);
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'src/assets/sass/marketing-tokens';

.post-page { padding-top: 110px; }

.post-hero {
  position: relative;
  padding: 2.5rem 0 2rem;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: -150px; left: 50%;
    transform: translateX(-50%);
    width: 700px; height: 500px;
    background: radial-gradient(circle, rgba(225, 78, 202, 0.12), transparent 60%);
    z-index: 0;
    pointer-events: none;
  }
  .container { position: relative; z-index: 1; }
}

.post-back-nav { margin-bottom: 1.5rem; }

.post-back {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: $muted-2;
  font-size: 0.85rem;
  text-decoration: none;
  &:hover { color: white; text-decoration: none; }
}

.post-back-icon { transform: rotate(180deg); }

.post-title { font-size: 2.5rem; }

.post-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  color: $muted-3;
  font-size: 0.85rem;
  margin: 0;
}

.post-meta-dot { color: $muted-3; }

.post-content {
  padding: 1rem 0 4rem;
}

/* Answer-first summary at the top of every post */
.post-takeaways {
  margin: 0 0 2.25rem;
  padding: 1.25rem 1.5rem;
  background: $card-bg;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-left: 3px solid $teal;
  border-radius: 12px;

  .post-takeaways-title {
    color: $teal;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    margin: 0 0 0.6rem;
  }

  ul {
    margin: 0;
    padding-left: 1.2rem;
    color: $muted;
    font-size: 0.98rem;
    line-height: 1.6;
    li { margin-bottom: 0.4rem; }
  }
}

/* Article typography. The post component renders inside .post-body. */
.post-content ::v-deep .post-body {
  color: $muted;
  font-size: 1.05rem;
  line-height: 1.75;

  .post-lead {
    color: white;
    font-size: 1.2rem;
    line-height: 1.65;
    margin-bottom: 2rem;
  }

  h2 {
    color: white;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.3;
    margin: 2.5rem 0 1rem;
  }

  h3 {
    color: white;
    font-size: 1.15rem;
    font-weight: 600;
    margin: 1.75rem 0 0.75rem;
  }

  p { margin-bottom: 1.1rem; }

  strong { color: white; font-weight: 600; }

  a {
    color: $primary-text;
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover { color: white; }
  }

  ul, ol {
    padding-left: 1.4rem;
    margin-bottom: 1.25rem;
    li { margin-bottom: 0.6rem; }
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.08);
    margin: 0.5rem 0 1.5rem;
  }

  .post-figure {
    margin: 1.5rem 0 2rem;
    img { margin: 0; }
    figcaption {
      margin-top: 0.6rem;
      color: $muted-3;
      font-size: 0.85rem;
      line-height: 1.5;
    }
  }

  blockquote {
    margin: 1.5rem 0;
    padding: 0.25rem 0 0.25rem 1.25rem;
    border-left: 3px solid $teal;
    color: $muted-2;
    font-style: italic;
  }
}

.post-faq {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);

  h2 {
    color: white;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1.25rem;
  }

  .post-faq-item {
    margin-bottom: 1.5rem;
    h3 { color: white; font-size: 1.05rem; font-weight: 600; margin-bottom: 0.4rem; }
    p { color: $muted; line-height: 1.7; margin: 0; }
  }
}

.post-missing {
  padding: 4rem 0 6rem;
  .post-missing-sub { color: $muted; margin-bottom: 1.5rem; }
}

@media (max-width: 768px) {
  .post-page { padding-top: 90px; }
  .post-title { font-size: 1.85rem; }
  .post-content ::v-deep .post-body { font-size: 1rem; }
}
</style>
